import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function ProductList() {
  const [sp, setSp] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minPrice, setMinPrice] = useState(sp.get("min_price") || "");
  const [maxPrice, setMaxPrice] = useState(sp.get("max_price") || "");
  const [sort, setSort] = useState(sp.get("sort") || "newest");
  const category = sp.get("category") || "";
  const q = sp.get("q") || "";

  const fetchProducts = async () => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    if (q) params.q = q;
    if (sp.get("min_price")) params.min_price = sp.get("min_price");
    if (sp.get("max_price")) params.max_price = sp.get("max_price");
    if (sort) params.sort = sort;
    try {
      const { data } = await api.get("/products", { params });
      setProducts(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); /* eslint-disable-next-line */ }, [sp.toString(), sort]);

  const applyFilters = () => {
    const s = new URLSearchParams(sp);
    if (minPrice) s.set("min_price", minPrice); else s.delete("min_price");
    if (maxPrice) s.set("max_price", maxPrice); else s.delete("max_price");
    setSp(s);
  };

  const setCategoryFilter = (c) => {
    const s = new URLSearchParams(sp);
    if (c) s.set("category", c); else s.delete("category");
    setSp(s);
  };

  const title = q ? `Results for "${q}"` : category ? category.charAt(0).toUpperCase()+category.slice(1) : "All Products";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#14A085]">Shop</div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#0F3D2E]" data-testid="product-list-title">{title}</h1>
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-44" data-testid="sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="price_asc">Price: Low to High</SelectItem>
            <SelectItem value="price_desc">Price: High to Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid md:grid-cols-[240px_1fr] gap-8">
        <aside className="space-y-6" data-testid="filters-panel">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#14A085] mb-2">Category</div>
            <div className="space-y-1">
              {[["","All"],["clothing","Clothing"],["electronics","Electronics"],["grocery","Grocery"]].map(([v,l]) => (
                <button
                  key={v}
                  onClick={() => setCategoryFilter(v)}
                  data-testid={`filter-cat-${v || "all"}`}
                  className={`block w-full text-left px-3 py-2 rounded-md text-sm ${category===v ? "bg-[#E8F5F2] text-[#0F3D2E] font-medium" : "text-[#3B5249] hover:bg-[#F9F9F7]"}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#14A085] mb-2">Price</div>
            <div className="space-y-2">
              <Input type="number" placeholder="Min ₹" value={minPrice} onChange={(e)=>setMinPrice(e.target.value)} data-testid="filter-min-price" />
              <Input type="number" placeholder="Max ₹" value={maxPrice} onChange={(e)=>setMaxPrice(e.target.value)} data-testid="filter-max-price" />
              <Button variant="outline" onClick={applyFilters} className="w-full rounded-full border-[#0F3D2E] text-[#0F3D2E]" data-testid="apply-filters">Apply</Button>
            </div>
          </div>
        </aside>

        <div>
          {loading ? (
            <div className="text-[#3B5249] py-12 text-center">Loading…</div>
          ) : products.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[#E2E8E5] bg-white p-10 text-center" data-testid="no-products">
              <p className="text-[#3B5249]">No products found matching your filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-testid="products-grid">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
